import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { addPhoto } from "../../redux/inspectionSlice";
import { api } from "../../services/api-service";
import ImageUpload from "../Form/ImageUpload";
import HeadingTwo from "../baseui/HeadingTwo";
import ImageUploadedList from "./ImageUploadedList";

const UploadInfo = () => {
  const dispatch = useDispatch();
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionStatus, setCompressionStatus] = useState("");

  // Optimized image compression function
  const compressImage = async (file, maxSizeMB = 2, maxWidthOrHeight = 1920) => {
    return new Promise((resolve, reject) => {
      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }

      const maxSizeBytes = maxSizeMB * 1024 * 1024;

      // If file is already smaller than max size, return it
      if (file.size <= maxSizeBytes) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidthOrHeight) {
              height = Math.round((height * maxWidthOrHeight) / width);
              width = maxWidthOrHeight;
            }
          } else {
            if (height > maxWidthOrHeight) {
              width = Math.round((width * maxWidthOrHeight) / height);
              height = maxWidthOrHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // Determine output format - use JPEG for better compression
          const outputType = file.type === 'image/png' ? 'image/jpeg' : file.type;
          const fileExtension = outputType === 'image/jpeg' ? 
            file.name.replace(/\.[^/.]+$/, '.jpg') : file.name;
          
          // Binary search for optimal quality
          const findOptimalQuality = async () => {
            let minQuality = 0.1;
            let maxQuality = 0.95;
            let bestBlob = null;
            let iterations = 0;
            const maxIterations = 8;
            
            while (iterations < maxIterations && maxQuality - minQuality > 0.05) {
              const testQuality = (minQuality + maxQuality) / 2;
              
              const blob = await new Promise((res) => {
                canvas.toBlob(res, outputType, testQuality);
              });
              
              if (blob.size <= maxSizeBytes) {
                bestBlob = blob;
                minQuality = testQuality;
              } else {
                maxQuality = testQuality;
              }
              
              iterations++;
            }
            
            // If still too large, try with lower quality
            if (!bestBlob || bestBlob.size > maxSizeBytes) {
              bestBlob = await new Promise((res) => {
                canvas.toBlob(res, outputType, minQuality);
              });
            }
            
            return bestBlob;
          };
          
          findOptimalQuality()
            .then((blob) => {
              // If compressed version is larger than original, use original
              if (blob.size >= file.size) {
                resolve(file);
                return;
              }
              
              const compressedFile = new File([blob], fileExtension, {
                type: outputType,
                lastModified: Date.now(),
              });
              
              console.log(`Compressed ${file.name}: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
              resolve(compressedFile);
            })
            .catch(reject);
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        
        img.src = e.target.result;
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  };

  const uploadAttachment = async (files) => {
    setIsCompressing(true);
    setCompressionStatus(`Compressing ${files.length} image(s)...`);
    
    try {
      // Compress all images first
      const compressionPromises = files.map(file => compressImage(file, 2, 1920));
      const compressedFiles = await Promise.all(compressionPromises);
      
      setCompressionStatus(`Uploading ${files.length} image(s)...`);
      
      // Upload compressed images
      let successCount = 0;
      for (let i = 0; i < compressedFiles.length; i++) {
        const compressedFile = compressedFiles[i];
        const formdata = new FormData();
        formdata.append("file", compressedFile);
        
        try {
          const response = await api.post("upload", formdata);
          if (response.ok) {
            dispatch(addPhoto(response.data.data));
            successCount++;
            setCompressionStatus(`Uploaded ${successCount}/${files.length}...`);
          }
        } catch (error) {
          console.error('Upload error for file:', compressedFile.name, error);
        }
      }
      
      setCompressionStatus("");
      setIsCompressing(false);
    } catch (error) {
      console.error('Compression error:', error);
      setCompressionStatus("Compression failed");
      setIsCompressing(false);
      
      // Clear error message after 3 seconds
      setTimeout(() => setCompressionStatus(""), 3000);
    }
  };

  return (
    <>
      <ImageUpload onUpload={uploadAttachment} disabled={isCompressing} />
      {compressionStatus && (
        <div style={{ 
          marginTop: '10px', 
          padding: '10px', 
          backgroundColor: isCompressing ? '#e3f2fd' : '#ffebee', 
          borderRadius: '4px',
          textAlign: 'center',
          color: isCompressing ? '#1976d2' : '#c62828',
          fontWeight: '500'
        }}>
          {compressionStatus}
        </div>
      )}
      <div className="mt-3">
        <HeadingTwo heading2="Uploaded"></HeadingTwo>
        <ImageUploadedList />
      </div>
    </>
  );
};

export default UploadInfo;